/* eslint-disable @typescript-eslint/no-explicit-any -- модель ExcelJS читается в том числе через внутренние поля
   (_rows, _cells, _columns, _merges), которых нет в его типах; файл перенесён из rp_back без изменения логики */
import { builtinFormat, formatNumber, formatText, LAST_DATE_SERIAL } from './format';
import { applyTint, INDEXED_PALETTE, indexedColor, safeColor } from './colors';
import { ZipArchive } from './zip';
import { applyConditionalFormats, type CfBlock, type CfRule, type CfValue, type DxfStyle } from './conditional';
import {
  emptyCell,
  type BorderEdge,
  type BorderStyle,
  type CellBorders,
  type CellLink,
  type CellStyle,
  type FontStyle,
  type HorizontalAlign,
  type Sheet,
  type SheetCell,
  type SheetImage,
  type SheetMerge,
  type TextRun,
  type VerticalAlign,
} from './types';

const EMU_PER_PX = 9525;
const MAX_UNCOMPRESSED_BYTES = 300 * 1024 * 1024;
const MAX_SHEET_CELLS = 3_000_000;
const MAX_PADDED_CELLS = 2_000_000;

const ERRORS: Record<string, string> = {
  '#NULL!': '#ПУСТО!',
  '#DIV/0!': '#ДЕЛ/0!',
  '#VALUE!': '#ЗНАЧ!',
  '#REF!': '#ССЫЛКА!',
  '#NAME?': '#ИМЯ?',
  '#NUM!': '#ЧИСЛО!',
  '#N/A': '#Н/Д',
  '#GETTING_DATA': '#GETTING_DATA',
};
const EXCELJS_BUILTINS: Record<string, number> = {
  'mm-dd-yy': 14,
  'd-mmm-yy': 15,
  'd-mmm': 16,
  'mmm-yy': 17,
  'm/d/yy "h":mm': 22,
  '#,##0 ;(#,##0)': 37,
  '#,##0 ;[Red](#,##0)': 38,
  '#,##0.00 ;(#,##0.00)': 39,
  '#,##0.00 ;[Red](#,##0.00)': 40,
  'mmss.0': 47,
};

const IMAGE_TYPES: Record<string, string> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  bmp: 'image/bmp',
  svg: 'image/svg+xml',
  webp: 'image/webp',
};

const BORDER_STYLES = new Set<string>([
  'thin',
  'medium',
  'thick',
  'dashed',
  'dotted',
  'double',
  'hair',
  'mediumDashed',
  'dashDot',
  'mediumDashDot',
  'dashDotDot',
  'mediumDashDotDot',
  'slantDashDot',
]);

const HORIZONTAL_ALIGN: Record<string, HorizontalAlign> = {
  general: 'general',
  left: 'left',
  center: 'center',
  centerContinuous: 'centerContinuous',
  right: 'right',
  fill: 'fill',
  justify: 'justify',
  distributed: 'distributed',
};

const VERTICAL_ALIGN: Record<string, VerticalAlign> = {
  top: 'top',
  middle: 'middle',
  bottom: 'bottom',
  justify: 'justify',
  distributed: 'distributed',
};
const VALUE_TYPE = {
  merge: 1,
  hyperlink: 5,
  formula: 6,
};

const FORMULA_LINK = /^=?HYPERLINK\(\s*"((?:[^"]|"")*)"/i;

interface Context {
  theme: string[];
  date1904: boolean;
  styles: Map<object, CellStyle>;
  fonts: Map<object, FontStyle>;
}

function parseXml(text: string): Document {
  return new DOMParser().parseFromString(text, 'application/xml');
}

function first(parent: Document | Element | null | undefined, name: string): Element | undefined {
  return parent?.getElementsByTagNameNS('*', name)[0];
}

function readThemeColors(xml?: string): string[] {
  const scheme = xml ? first(parseXml(xml), 'clrScheme') : undefined;
  if (!scheme) {
    return [];
  }
  return Array.from(scheme.children).map((entry) => {
    const srgb = first(entry, 'srgbClr');
    if (srgb) {
      return safeColor(`#${srgb.getAttribute('val')}`) ?? '#000000';
    }
    const sys = first(entry, 'sysClr');
    return (sys && safeColor(`#${sys.getAttribute('lastClr')}`)) ?? '#000000';
  });
}

function readColor(value: any, context: Context): string | undefined {
  if (!value) {
    return undefined;
  }
  const tint = Number(value.tint ?? 0);
  if (typeof value.argb === 'string') {
    const rgb = safeColor(`#${value.argb.slice(-6)}`);
    return rgb ? applyTint(rgb, tint) : undefined;
  }
  if (value.theme !== undefined) {
    const index = Number(value.theme);
    const color = context.theme[[1, 0, 3, 2][index] ?? index];
    return color ? applyTint(color, tint) : undefined;
  }
  if (value.indexed !== undefined) {
    const color = indexedColor(Number(value.indexed), INDEXED_PALETTE);
    return color ? applyTint(color, tint) : undefined;
  }
  return undefined;
}

function readFont(value: any, context: Context): FontStyle {
  if (!value) {
    return {};
  }
  const cached = context.fonts.get(value);
  if (cached) {
    return cached;
  }
  const result: FontStyle = {};
  if (value.name) {
    result.fontName = value.name;
  }
  if (value.size) {
    result.fontSize = value.size;
  }
  if (value.bold !== undefined) {
    result.bold = Boolean(value.bold);
  }
  if (value.italic !== undefined) {
    result.italic = Boolean(value.italic);
  }
  if (value.underline && value.underline !== 'none') {
    result.underline = String(value.underline).startsWith('double') ? 'double' : 'single';
  }
  if (value.strike !== undefined) {
    result.strike = Boolean(value.strike);
  }
  const color = readColor(value.color, context);
  if (color) {
    result.color = color;
  }
  if (value.vertAlign === 'superscript' || value.vertAlign === 'subscript') {
    result.vertAlign = value.vertAlign;
  }
  context.fonts.set(value, result);
  return result;
}

function readFill(value: any, context: Context, differential = false): string | undefined {
  if (!value) {
    return undefined;
  }
  if (value.type === 'gradient') {
    return readColor(value.stops?.[0]?.color, context);
  }
  if (value.type !== 'pattern') {
    return undefined;
  }
  const pattern = value.pattern ?? (differential ? 'solid' : 'none');
  if (pattern === 'none') {
    return undefined;
  }
  const foreground = readColor(value.fgColor, context);
  const background = readColor(value.bgColor, context);
  return differential ? (background ?? foreground) : (foreground ?? background);
}

function readEdge(value: any, context: Context): BorderEdge | undefined {
  if (!value?.style || !BORDER_STYLES.has(value.style)) {
    return undefined;
  }
  return { style: value.style as BorderStyle, color: readColor(value.color, context) };
}

function readBorders(value: any, context: Context): CellBorders | undefined {
  if (!value) {
    return undefined;
  }
  const result: CellBorders = {
    top: readEdge(value.top, context),
    right: readEdge(value.right, context),
    bottom: readEdge(value.bottom, context),
    left: readEdge(value.left, context),
  };
  const diagonal = readEdge(value.diagonal, context);
  if (diagonal && value.diagonal?.up) {
    result.diagonalUp = diagonal;
  }
  if (diagonal && value.diagonal?.down) {
    result.diagonalDown = diagonal;
  }
  return Object.values(result).some(Boolean) ? result : undefined;
}

function compact<T extends object>(value: T): T {
  Object.keys(value).forEach((key) => {
    if ((value as Record<string, unknown>)[key] === undefined) {
      delete (value as Record<string, unknown>)[key];
    }
  });
  return value;
}

function readStyle(value: any, context: Context): CellStyle | undefined {
  if (!value || !Object.keys(value).length) {
    return undefined;
  }
  const cached = context.styles.get(value);
  if (cached) {
    return cached;
  }
  const alignment = value.alignment ?? {};
  const angle = alignment.textRotation === 'vertical' ? 255 : Number(alignment.textRotation) || 0;
  const result = compact<CellStyle>({
    ...readFont(value.font, context),
    background: readFill(value.fill, context),
    borders: readBorders(value.border, context),
    align: HORIZONTAL_ALIGN[alignment.horizontal ?? ''],
    valign: VERTICAL_ALIGN[alignment.vertical ?? ''],
    wrap: alignment.wrapText || undefined,
    indent: alignment.indent || undefined,
    rotation: (angle < 0 ? 90 - angle : angle) || undefined,
    shrink: alignment.shrinkToFit || undefined,
  });
  context.styles.set(value, result);
  return result;
}

function formatCodeOf(numFmt?: string): string | undefined {
  if (!numFmt) {
    return undefined;
  }
  const builtin = EXCELJS_BUILTINS[numFmt];
  return builtin === undefined ? numFmt : builtinFormat(builtin);
}

function numberCell(value: number, formatCode: string | undefined, context: Context): SheetCell {
  const formatted = formatNumber(value, formatCode, context.date1904);
  return {
    kind: 'number',
    value,
    text: formatted.text,
    formatColor: formatted.color,
    fill: formatted.fill,
    formatCode,
  };
}

function readValue(raw: any, formatCode: string | undefined, context: Context): SheetCell {
  if (raw === null || raw === undefined || raw === '') {
    return emptyCell();
  }
  if (raw instanceof Date) {
    const serial = 25569 + raw.getTime() / 86400000 - (context.date1904 ? 1462 : 0);
    if (!(serial >= 0 && serial < LAST_DATE_SERIAL)) {
      return { kind: 'number', text: '#'.repeat(10), formatCode };
    }
    return numberCell(serial, formatCode, context);
  }
  if (typeof raw === 'number') {
    return numberCell(raw, formatCode, context);
  }
  if (typeof raw === 'boolean') {
    return { kind: 'boolean', value: raw, text: raw ? 'ИСТИНА' : 'ЛОЖЬ' };
  }
  if (typeof raw === 'string') {
    const formatted = formatText(raw, formatCode);
    return { kind: 'text', value: raw, text: formatted.text, formatColor: formatted.color, fill: formatted.fill };
  }
  if (raw.error) {
    return { kind: 'error', value: raw.error, text: ERRORS[raw.error] ?? raw.error };
  }
  if (Array.isArray(raw.richText)) {
    const runs: TextRun[] = raw.richText.map((run: any) => ({
      text: String(run.text ?? ''),
      font: run.font ? readFont(run.font, context) : undefined,
    }));
    const text = runs.map((run) => run.text).join('');
    return { kind: text ? 'text' : 'empty', value: text, text, runs };
  }
  return emptyCell();
}

function linkTarget(target: string): CellLink {
  if (target.startsWith('#')) {
    return { location: target.slice(1) };
  }
  return { url: target };
}

function readCell(source: any, context: Context): SheetCell {
  const formatCode = formatCodeOf(source.numFmt);
  const raw = source.value;
  let cell: SheetCell;
  let link: CellLink | undefined;
  if (source.type === VALUE_TYPE.formula) {
    cell = readValue(raw?.result, formatCode, context);
    if (source.formula) {
      cell.formula = raw?.shareType === 'array' ? `{=${source.formula}}` : `=${source.formula}`;
    }
  } else if (source.type === VALUE_TYPE.hyperlink) {
    cell = readValue(raw?.text, formatCode, context);
    if (raw?.hyperlink) {
      link = { ...linkTarget(String(raw.hyperlink)), tooltip: raw.tooltip };
    }
  } else if (source.type === VALUE_TYPE.merge) {
    cell = emptyCell();
  } else {
    cell = readValue(raw, formatCode, context);
  }

  const formulaLink = cell.formula?.match(FORMULA_LINK);
  if (formulaLink) {
    link = linkTarget(formulaLink[1].replace(/""/g, '"'));
  }
  if (link) {
    cell.hyperlink = link;
  }
  const style = readStyle(source.style, context);
  if (style && Object.keys(style).length) {
    cell.style = style;
  }
  return cell;
}

function cellAddress(ref: string): { row: number; col: number } {
  const letters = ref.replace(/[^A-Za-z]/g, '').toUpperCase();
  let col = 0;
  for (const letter of letters) {
    col = col * 26 + (letter.charCodeAt(0) - 64);
  }
  return { row: Number(ref.replace(/[^\d]/g, '')) - 1, col: col - 1 };
}

function parseRange(ref: string): SheetMerge | null {
  const [from, to = from] = ref.replace(/\$/g, '').split(':');
  if (!from) {
    return null;
  }
  const hasRow = (part: string) => /\d/.test(part);
  const hasCol = (part: string) => /[A-Za-z]/.test(part);
  const start = cellAddress(from);
  const end = cellAddress(to);
  return {
    rowFrom: hasRow(from) ? start.row : 0,
    rowTo: hasRow(to) ? end.row : 1048575,
    colFrom: hasCol(from) ? start.col : 0,
    colTo: hasCol(to) ? end.col : 16383,
  };
}

function readDxf(value: any, context: Context): DxfStyle | undefined {
  if (!value) {
    return undefined;
  }
  return compact({
    font: value.font ? readFont(value.font, context) : undefined,
    background: readFill(value.fill, context, true),
    borders: readBorders(value.border, context),
  });
}

function readCfValues(values: any[] | undefined): CfValue[] {
  return (values ?? []).map((value) => ({
    type: value.type ?? 'min',
    value: value.value === undefined || Number.isNaN(value.value) ? undefined : String(value.value),
    gte: true,
  }));
}

function readConditionalFormats(sheet: any, context: Context): CfBlock[] {
  return (sheet.conditionalFormattings ?? []).map((block: any) => {
    const ranges = String(block.ref ?? '')
      .split(/\s+/)
      .map(parseRange)
      .filter((range): range is SheetMerge => Boolean(range));
    const rules: CfRule[] = (block.rules ?? []).map((rule: any) => {
      let type: string = rule.type ?? '';
      let operator: string | undefined = rule.operator;
      const formulas: string[] = rule.formulae ?? [];
      if (type === 'containsText') {
        type = operator ?? type;
        operator = undefined;
      }
      if (['containsText', 'notContainsText', 'beginsWith', 'endsWith'].includes(type) && formulas.length) {
        type = 'expression';
      }
      const result: CfRule = {
        type,
        priority: rule.priority ?? 0,
        stopIfTrue: false,
        dxf: readDxf(rule.style, context),
        operator,
        formulas,
        rank: rule.rank,
        percent: Boolean(rule.percent),
        bottom: Boolean(rule.bottom),
        aboveAverage: rule.aboveAverage !== false,
        equalAverage: false,
        timePeriod: rule.timePeriod,
      };
      if (type === 'colorScale') {
        result.colorScale = {
          values: readCfValues(rule.cfvo),
          colors: (rule.color ?? []).map((color: any) => readColor(color, context) ?? '#FFFFFF'),
        };
      }
      if (type === 'dataBar') {
        result.dataBar = {
          values: readCfValues(rule.cfvo),
          color: readColor(rule.color, context) ?? '#638EC6',
          showValue: rule.showValue !== false,
          minLength: rule.minLength ?? 10,
          maxLength: rule.maxLength ?? 90,
        };
      }
      if (type === 'iconSet') {
        result.iconSet = {
          name: rule.iconSet ?? '3TrafficLights1',
          values: readCfValues(rule.cfvo),
          reverse: Boolean(rule.reverse),
          showValue: rule.showValue !== false,
        };
      }
      return result;
    });
    return { ranges, rules };
  });
}

function readImages(workbook: any, sheet: any): SheetImage[] {
  return sheet.getImages().flatMap((image: any): SheetImage[] => {
    const media = workbook.getImage(Number(image.imageId));
    const type = IMAGE_TYPES[String(media?.extension ?? '').toLowerCase()];
    const { tl, br, ext } = image.range ?? {};
    if (!media?.buffer || !type || !tl) {
      return [];
    }
    const data = new Blob([media.buffer], { type });
    const anchor = (point: any) => ({
      row: point.nativeRow ?? 0,
      col: point.nativeCol ?? 0,
      rowOffset: (point.nativeRowOff ?? 0) / EMU_PER_PX,
      colOffset: (point.nativeColOff ?? 0) / EMU_PER_PX,
    });
    if (br) {
      return [{ data, from: anchor(tl), to: anchor(br) }];
    }
    return [{ data, from: anchor(tl), width: ext?.width ?? 0, height: ext?.height ?? 0 }];
  });
}
function readDefaultFont(bytes: Uint8Array, context: Context): FontStyle {
  const fallback: FontStyle = { fontName: 'Calibri', fontSize: 11 };
  let text: string | null = null;
  try {
    text = new ZipArchive(bytes, MAX_UNCOMPRESSED_BYTES).text('xl/styles.xml');
  } catch {
    return fallback;
  }
  if (!text) {
    return fallback;
  }
  const doc = parseXml(text);
  const normal = first(first(doc, 'cellStyleXfs'), 'xf');
  const fonts = Array.from(first(doc, 'fonts')?.children ?? []);
  const font = fonts[Number(normal?.getAttribute('fontId') ?? 0)] ?? fonts[0];
  if (!font) {
    return fallback;
  }
  const value = (name: string) => first(font, name)?.getAttribute('val');
  const color = first(font, 'color');
  return compact({
    fontName: value('name') ?? fallback.fontName,
    fontSize: Number(value('sz')) || fallback.fontSize,
    bold: first(font, 'b') ? value('b') !== '0' : undefined,
    italic: first(font, 'i') ? value('i') !== '0' : undefined,
    color: color
      ? readColor(
          {
            argb: color.getAttribute('rgb') ?? undefined,
            theme: color.getAttribute('theme') ?? undefined,
            indexed: color.getAttribute('indexed') ?? undefined,
            tint: color.getAttribute('tint') ?? undefined,
          },
          context,
        )
      : undefined,
  });
}

function parseSheet(workbook: any, sheet: any, context: Context, defaultFont: FontStyle): Sheet {
  const rows: SheetCell[][] = [];
  const rowHeights: (number | undefined)[] = [];
  const hiddenRows: boolean[] = [];
  const rowStyles = new Map<number, CellStyle>();
  let maxColumn = 0;
  let cellBudget = MAX_SHEET_CELLS;

  const sourceRows: any[] = sheet._rows ?? [];
  for (let index = 0; index < sourceRows.length && cellBudget > 0; index++) {
    const source = sourceRows[index];
    if (!source) {
      continue;
    }
    const sourceCells: any[] = source._cells ?? [];
    cellBudget -= sourceCells.length;
    const cells: SheetCell[] = [];
    for (let col = 0; col < sourceCells.length; col++) {
      cells.push(sourceCells[col] ? readCell(sourceCells[col], context) : emptyCell());
    }
    while (rows.length < index) {
      rows.push([]);
    }
    rows[index] = cells;
    maxColumn = Math.max(maxColumn, cells.length);
    if (source.height) {
      rowHeights[index] = source.height;
    }
    if (source.hidden) {
      hiddenRows[index] = true;
    }
    const style = readStyle(source.style, context);
    if (style && Object.keys(style).length) {
      rowStyles.set(index, style);
    }
  }

  const columnWidths: (number | undefined)[] = [];
  const hiddenColumns: boolean[] = [];
  const columnStyles = new Map<number, CellStyle>();
  (sheet._columns ?? []).forEach((column: any, index: number) => {
    if (!column) {
      return;
    }
    if (column.width !== undefined) {
      columnWidths[index] = column.width;
    }
    if (column.hidden) {
      hiddenColumns[index] = true;
    }
    const style = readStyle(column.style, context);
    if (style && Object.keys(style).length) {
      columnStyles.set(index, style);
    }
  });

  const padRows = rows.length * maxColumn <= MAX_PADDED_CELLS;
  rows.forEach((cells, row) => {
    if (padRows) {
      while (cells.length < maxColumn) {
        cells.push(emptyCell());
      }
    }
    cells.forEach((cell, col) => {
      if (cell.style || cell.kind !== 'empty') {
        return;
      }
      const style = rowStyles.get(row) ?? columnStyles.get(col);
      if (style) {
        cell.style = style;
      }
    });
  });

  const merges: SheetMerge[] = Object.values(sheet._merges ?? {}).map((merge: any) => ({
    rowFrom: merge.model.top - 1,
    rowTo: merge.model.bottom - 1,
    colFrom: merge.model.left - 1,
    colTo: merge.model.right - 1,
  }));

  applyConditionalFormats(rows, readConditionalFormats(sheet, context), context.date1904);

  const view = sheet.views?.[0] ?? {};
  const frozen = view.state === 'frozen';
  return {
    name: sheet.name,
    rows,
    merges,
    columnWidths,
    hiddenColumns,
    rowHeights,
    hiddenRows,
    defaultColumnWidth: sheet.properties?.defaultColWidth,
    baseColumnWidth: 8,
    defaultRowHeight: sheet.properties?.defaultRowHeight || undefined,
    showGridLines: view.showGridLines !== false,
    frozenRows: frozen ? (view.ySplit ?? 0) : 0,
    frozenColumns: frozen ? (view.xSplit ?? 0) : 0,
    zoom: (view.zoomScale ?? 100) / 100 || 1,
    images: readImages(workbook, sheet),
    tabColor: readColor(sheet.properties?.tabColor, context),
    defaultFont,
    activeCell: view.activeCell ? cellAddress(view.activeCell) : undefined,
    date1904: context.date1904,
  };
}

export async function parseXlsx(bytes: Uint8Array): Promise<Sheet[]> {
  const module: any = await import('exceljs');
  const ExcelJS = module.default ?? module;
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength));

  const context: Context = {
    theme: readThemeColors(workbook._themes?.theme1),
    date1904: Boolean(workbook.properties?.date1904),
    styles: new Map(),
    fonts: new Map(),
  };
  const defaultFont = readDefaultFont(bytes, context);
  const activeTab = workbook.views?.[0]?.activeTab ?? 0;

  const sheets: Sheet[] = [];
  workbook.worksheets.forEach((source: any, index: number) => {
    if (source.state && source.state !== 'visible') {
      return;
    }
    const sheet = parseSheet(workbook, source, context, defaultFont);
    if (index === activeTab) {
      sheet.active = true;
    }
    sheets.push(sheet);
  });
  return sheets;
}
